import type { Column } from "@tanstack/react-table";
import { Filter } from "lucide-react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";
import { Input } from "@/shared/ui/input/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select/Select";

import { columnLabel } from "../columnUtils";

/** Per-column header filter: a popover with a text box or a select dropdown. */
export function ColumnFilterControl<TData, TValue>({ column }: { column: Column<TData, TValue> }) {
  const raw = column.getFilterValue();
  const value = typeof raw === "string" ? raw : "";
  const active = value !== "";
  const label = columnLabel(column);
  const variant = column.columnDef.meta?.filterVariant ?? "text";
  const options = column.columnDef.meta?.filterOptions ?? [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn("h-6 w-6 shrink-0", active ? "text-primary" : "text-muted-foreground/70")}
          aria-label={`Filter ${label}`}
          title={`Filter ${label}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className={cn("h-3.5 w-3.5", active && "fill-current")} aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 space-y-2 p-2">
        <p className="px-1 text-xs font-medium text-muted-foreground">Filter {label}</p>
        {variant === "select" ? (
          <Select
            value={value || "__all__"}
            onValueChange={(v) => column.setFilterValue(v === "__all__" ? undefined : v)}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder={`All ${label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={value}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            placeholder="Contains…"
            className="h-8"
          />
        )}
        {active ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start text-muted-foreground"
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear filter
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
